/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
@file:OptIn(ExperimentalSerializationApi::class, ExperimentalSettingsApi::class)

package studio.oddpods.cauce.core.datastore

import com.russhwolf.settings.ExperimentalSettingsApi
import com.russhwolf.settings.Settings
import com.russhwolf.settings.serialization.decodeValue
import com.russhwolf.settings.serialization.encodeValue
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.withContext
import kotlinx.serialization.ExperimentalSerializationApi
import studio.oddpods.cauce.core.base.common.manager.DispatcherManager
import studio.oddpods.cauce.core.model.notification.NotificationPreferences

private const val NOTIFICATION_PREFERENCES_KEY = "notification_preferences_key"

class NotificationPreferencesRepositoryImpl(
    private val settings: Settings,
    private val dispatcher: DispatcherManager,
) : NotificationPreferencesRepository {

    private val _preferences = MutableStateFlow(
        settings.decodeValue(
            key = NOTIFICATION_PREFERENCES_KEY,
            serializer = NotificationPreferences.serializer(),
            defaultValue = NotificationPreferences.DEFAULT,
        ),
    )

    override val preferences: StateFlow<NotificationPreferences>
        get() = _preferences.asStateFlow()

    override suspend fun update(
        transform: (NotificationPreferences) -> NotificationPreferences,
    ) = withContext(dispatcher.io) {
        val current = settings.getNotificationPreferences()
        val updated = transform(current)
        settings.putNotificationPreferences(updated)
        _preferences.value = updated
    }
}

private fun Settings.getNotificationPreferences(): NotificationPreferences {
    return decodeValue(
        key = NOTIFICATION_PREFERENCES_KEY,
        serializer = NotificationPreferences.serializer(),
        defaultValue = NotificationPreferences.DEFAULT,
    )
}

private fun Settings.putNotificationPreferences(preferences: NotificationPreferences) {
    encodeValue(
        key = NOTIFICATION_PREFERENCES_KEY,
        serializer = NotificationPreferences.serializer(),
        value = preferences,
    )
}
